import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useState, useRef, PropsWithChildren, useEffect } from 'react';

import { AddressMismatchDialog } from '@/components/uploader/AddressMismatchDialog';
import { useAuthContext } from '@/context/AuthProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useSnackbar } from '@/context/SnackbarProvider';
import { useWebSocketContext } from '@/context/WebSocketProvider';
import { ADMIN_PROPERTIES_QUERY_KEY } from '@/hooks/admin/useAdminProperties';
import { useGetDocumentProcessingJobById } from '@/hooks/document-processing-jobs';
import { useStartPropertyProcessing } from '@/hooks/properties';
import { useFileUploadWithPresigning } from '@/hooks/useFileUploadWithPresigning';
import { useLogger } from '@/hooks/useLogger';
import { Document } from '@/models/Document';
import { DocumentProcessingJobType, JobState } from '@/models/DocumentProcessingJob';
import {
  AddressMismatchPayload,
  AiProgressMessage,
  AiProgressMessageCode,
  IssueCount,
} from '@/models/DocumentProgressMessage';
import { IssueUrgency } from '@/models/Issue';
import { isApiError } from '@/services/api';
import { searchDocumentProcessingJobsByUser } from '@/services/api/document-processing-jobs/api';
import { deleteDocumentRecord } from '@/services/api/document-records/api';
import { cancelPendingUpload, confirmAddressOverride } from '@/services/api/documents/api';
import { useTranslationPrefix } from '@/translations/i18n';

export interface StreamingIssue {
  title: string;
  category: string;
  description: string;
  urgency: IssueUrgency;
}

interface UploaderContextProps {
  uploadedDocuments: Document[];
  removeUploadedDocument: (document: Document) => void;
  startFileUpload: (files: File[]) => void;
  uploadProgress: number;
  isFileUploadInProgress: boolean;
  isFileUploadSuccess: boolean | undefined;
  startPropertyProcessing: () => void;
  /**
   * Lightweight trigger for upload flows that don't go through startFileUpload
   * (e.g. AddPropertyModal, which uploads directly to S3 and links docs to a
   * brand-new property). Kicks the sidebar ProcessingIndicator to step 1; the
   * existing WebSocket handlers progress through steps 2-5 automatically.
   */
  beginProcessingIndicator: () => void;
  resetProcessing: () => void;
  lastProcessedPropertyId?: string;
  isPropertyProcessingInProgress: boolean;
  isPropertyProcessingSuccess: boolean;
  isPostPropertyProcessingDone: boolean;
  stepProcessing: number;
  streamingIssue?: StreamingIssue;
  streamingProperty?: string;
  streamingIssuesCount?: IssueCount;
}

export const UploaderContext = createContext<UploaderContextProps | null>(null);

// Module-level dedup sets. We were using useRef Sets inside the component,
// but in dev (React StrictMode + Vite HMR) component instances get re-created
// and the refs reset. A module-level Set survives both, so duplicate WS
// deliveries (SNS at-least-once + multiple active connections) are reliably
// filtered. Keyed on combinedETag because it's always present and uniquely
// identifies an upload run.
//
// Bounded to MAX_DEDUP_KEYS to prevent unbounded growth in long-lived tabs.
// FIFO eviction is fine because duplicate WS deliveries arrive within
// milliseconds of each other — anything older than the most recent ~200
// uploads is no longer at risk of being re-delivered.
const MAX_DEDUP_KEYS = 200;
const handledMismatchKeys = new Set<string>();
const handledDuplicateKeys = new Set<string>();
const rememberDedupKey = (set: Set<string>, key: string) => {
  if (set.size >= MAX_DEDUP_KEYS) {
    const oldest = set.values().next().value;
    if (oldest !== undefined) set.delete(oldest);
  }
  set.add(key);
};

export const useUploaderContext = () => {
  const context = useContext(UploaderContext);

  if (!context) throw new Error('useUploaderContext context must be used inside UploaderProvider');

  return context;
};

export const UploaderProvider = ({ children }: PropsWithChildren) => {
  const t = useTranslationPrefix('uploader');
  const toastT = useTranslationPrefix('toasts');
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const { properties, selectedProperty, propertyIssues } = usePropertiesContext();
  const { aiProgressMessage, connectionAttempt } = useWebSocketContext();
  const { showAlert } = useSnackbar();
  const logger = useLogger('UploaderProvider');

  const {
    startFileUpload: triggerFileUpload,
    uploadProgress,
    inProgress: isFileUploadInProgress,
    isSuccess: isFileUploadSuccess,
    result: fileUploadResult,
    // Fallback to empty string when no property is selected — the API rejects
    // empty propertyId with a 400, so an upload without context fails fast.
  } = useFileUploadWithPresigning(undefined, selectedProperty?.property.id ?? '');

  const {
    // startPropertyProcessing: triggerProcessingJob,
    propertyProcessingJob: triggeredProcessingJob,
  } = useStartPropertyProcessing();

  const { documentProcessingJob } = useGetDocumentProcessingJobById(
    triggeredProcessingJob?.id || '',
    {
      poll: true,
    },
  );

  const [uploadedDocuments, setUploadedDocuments] = useState<Document[]>([]);
  const [isPropertyProcessingSuccess, setIsPropertyProcessingSuccess] = useState(false);
  const [isPropertyProcessingInProgress, setIsPropertyProcessingInProgress] = useState(false);
  const [isPostPropertyProcessingStarted, setIsPostPropertyProcessingStarted] = useState(false);
  const [stepProcessing, setStepProcessing] = useState(0);
  const [isPostPropertyProcessingDone, setIsPostPropertyProcessingDone] = useState(false);
  const [streamingProperty, setStreamingProperty] = useState<string | undefined>(undefined);
  const [streamingIssuesCount, setStreamingIssuesCount] = useState<IssueCount | undefined>(
    undefined,
  );
  const [propertyIdsAwaitingNotification, setPropertyIdsAwaitingNotification] = useState<string[]>(
    [],
  );
  const [streamingIssue, setStreamingIssue] = useState<StreamingIssue | undefined>(undefined);
  const [lastProcessedPropertyId, setLastProcessedPropertyId] = useState<string | undefined>(
    undefined,
  );
  const isMockMismatch =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get('mock') === 'address-mismatch';
  const [addressMismatchPayload, setAddressMismatchPayload] = useState<
    AddressMismatchPayload | undefined
  >(
    isMockMismatch
      ? {
          documentId: 'mock-doc',
          documentRecordId: 'mock-record',
          documentName: 'Carter Roof Inspection Report.pdf',
          propertyId: 'mock-prop',
          extractedAddress: '4157 Valle Del Sol, Bonsall, CA 92003',
          propertyAddress: '5248 Carter Avenue, San Jose, CA 95118',
          addressConfidence: 0.95,
          confidenceThreshold: 0.5,
          reason: 'mock',
        }
      : undefined,
  );
  const [isOverrideSubmitting, setIsOverrideSubmitting] = useState(false);
  const [overrideError, setOverrideError] = useState<string | undefined>(undefined);
  // Epoch-ms timestamp set when an upload/processing session starts.
  // Used by the polling fallback to scope which jobs belong to this session.
  const [processingStartTime, setProcessingStartTime] = useState<number | undefined>();

  const isProcessingActive = stepProcessing >= 1 && stepProcessing < 5;
  const userId = user?.id ?? '';
  // Tracks the last connectionAttempt value we already synced for, so the
  // useEffect below only fires on new reconnect events, not on other dep changes.
  const lastSyncedAttempt = useRef(-1);
  // Ref mirror of isPropertyProcessingSuccess — lets the async fetchQuery callback
  // read the current value without going stale mid-flight.
  const isProcessingSuccessRef = useRef(isPropertyProcessingSuccess);

  const startFileUpload = (files: File[]) => {
    if (files.length === 0) {
      return;
    }
    setStepProcessing(1);
    setProcessingStartTime(Date.now());
    triggerFileUpload(files);
    setIsPropertyProcessingSuccess(false);
    setLastProcessedPropertyId(undefined);
    setStreamingProperty(undefined); // Reset streaming text on new upload
    setStreamingIssuesCount(undefined); // Reset streaming number of issues on new upload
    // Stale address-mismatch modal from a prior upload should not linger when
    // the user starts a new one. The current upload either won't mismatch (no
    // modal) or will fire a fresh mismatch SNS for a new documentId (modal
    // re-opens with current data).
    setAddressMismatchPayload(undefined);
    setOverrideError(undefined);
  };

  const startPropertyProcessing = () => {
    if (uploadedDocuments.length === 0) {
      return;
    }

    setIsPropertyProcessingInProgress(true);
    setIsPropertyProcessingSuccess(false);
    setIsPostPropertyProcessingDone(false);
  };

  const handleDocumentProcessingError = (errorMessage?: string) => {
    setStepProcessing(0);
    setIsPropertyProcessingInProgress(false);
    setIsPropertyProcessingSuccess(false);
    setUploadedDocuments([]);
    setStreamingProperty(undefined); // Reset streaming text on error

    if (errorMessage) {
      logger.error('Document processing error', { errorMessage });
    }
    showAlert(t('processing_error'), 'error');
  };

  const handleDocumentProcessingSuccess = (propertyId?: string) => {
    setIsPropertyProcessingInProgress(false);
    setIsPropertyProcessingSuccess(true);
    setLastProcessedPropertyId(propertyId);
    setUploadedDocuments([]);

    const isFirstProperty = !properties || properties.length === 0;
    if (!isFirstProperty) {
      setPropertyIdsAwaitingNotification((prev) => (propertyId ? [...prev, propertyId] : prev));
    }

    queryClient.invalidateQueries({ queryKey: ['properties'] });

    const resolvedPropertyId = propertyId || selectedProperty?.property.id;
    if (resolvedPropertyId) {
      queryClient.invalidateQueries({
        queryKey: ['issues-for-property', resolvedPropertyId],
      });
      // hauser-eq-results drives ConditionScoreView, InspectionReportView, and
      // every score/KPI card. Its hook only polls while status is transient, so
      // without an explicit invalidation here the UI stays frozen on stale
      // scores after upload completes.
      queryClient.invalidateQueries({
        queryKey: ['hauser-eq-results', resolvedPropertyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['document-records', resolvedPropertyId],
      });
      queryClient.invalidateQueries({ queryKey: ['nhd', resolvedPropertyId] });
      // HOA detail/checklist data is written by the HOA extractor lambdas at
      // the end of processing. Without invalidating this query, react-query's
      // five-minute stale window leaves the mounted HOA page showing the
      // pre-upload response until the user hard-refreshes the browser.
      queryClient.invalidateQueries({ queryKey: ['hoa-checklist', resolvedPropertyId] });
      queryClient.invalidateQueries({
        queryKey: ['get-document-processing-jobs', resolvedPropertyId],
      });
    }

    setIsPostPropertyProcessingStarted(true);
  };

  const handlePostProcessingSuccess = () => {
    if (propertyIdsAwaitingNotification.length === 0) {
      showAlert(t('processing_success'), 'success');
      setIsPostPropertyProcessingStarted(false);
      setIsPostPropertyProcessingDone(true);
      setStepProcessing(5);
      return;
    }
    setStepProcessing(5);

    const remainingPropertyIds: string[] = [];

    propertyIdsAwaitingNotification.forEach((propertyId) => {
      const userProperty = properties?.find(
        (userProperty) => userProperty.property.id === propertyId,
      );

      if (!userProperty?.property) {
        remainingPropertyIds.push(propertyId);
        return;
      }

      showAlert(
        userProperty.property.address
          ? t('property_added', { address: userProperty.property.address })
          : t('processing_success'),
        'success',
      );
    });

    setPropertyIdsAwaitingNotification(remainingPropertyIds);

    if (remainingPropertyIds.length === 0) {
      setIsPostPropertyProcessingStarted(false);
      setIsPostPropertyProcessingDone(true);
    }
  };

  const removeUploadedDocument = (document: Document) => {
    setUploadedDocuments((previousValue) =>
      previousValue.filter((uploadedDocument) => uploadedDocument.id !== document.id),
    );
  };

  useEffect(() => {
    if (isFileUploadInProgress) return;
    if (isFileUploadSuccess === false) {
      handleDocumentProcessingError('File upload failed. Please try again.');
      return;
    }
    if (isFileUploadSuccess && fileUploadResult) {
      setUploadedDocuments((previousValue) => [...previousValue, ...fileUploadResult]);
    }
  }, [isFileUploadInProgress, isFileUploadSuccess, fileUploadResult]);

  useEffect(() => {
    if (uploadedDocuments.length > 0 && !isPropertyProcessingInProgress) {
      startPropertyProcessing();
    }
  }, [uploadedDocuments, isPropertyProcessingInProgress]);

  useEffect(() => {
    if (!documentProcessingJob || documentProcessingJob.state === JobState.inProgress) {
      return;
    }

    const errorMessage = documentProcessingJob.messages?.[0]?.text;

    if (documentProcessingJob.state === JobState.failed) {
      logger.error(`property processing failed, jobId: ${documentProcessingJob.id}`);
      handleDocumentProcessingError(errorMessage);
      return;
    }

    if (documentProcessingJob.state === JobState.completed) {
      logger.debug('property processing is complete');
      handleDocumentProcessingSuccess(documentProcessingJob.propertyId);
    }
  }, [documentProcessingJob]);

  useEffect(() => {
    if (!aiProgressMessage) {
      return;
    }
    const {
      statusCode,
      userMessage,
      end,
      propertyId,
      additionalPayload,
      code,
      issueCount,
      propertyAddress,
      streamingIssue,
    } = aiProgressMessage;
    const isError = statusCode >= 400;
    const isSuccess = statusCode >= 200 && statusCode < 300;
    const resolvedPropertyId = propertyId ?? (additionalPayload?.propertyId as string | undefined);
    const streamingPropertyValue = propertyAddress as string | undefined;
    const issueCountValue = issueCount as IssueCount | undefined;
    const issueValue = streamingIssue as StreamingIssue | undefined;

    if (code === AiProgressMessageCode.DUPLICATE_FOUND) {
      // Same dedup pattern as DOCUMENT_ADDRESS_MISMATCH below: SNS at-least-
      // once + multi-connection fan-out can deliver this twice on the socket
      // for one upload run.
      const dupKey = aiProgressMessage.combinedETag;
      if (dupKey && handledDuplicateKeys.has(dupKey)) return;
      if (dupKey) rememberDedupKey(handledDuplicateKeys, dupKey);
      logger.info('Duplicate file detected — showing toast', { userMessage });
      showAlert(toastT('file.duplicate_upload'), 'warning');
      setStepProcessing(0);
      return;
    }

    if (code === AiProgressMessageCode.DOCUMENT_ADDRESS_MISMATCH) {
      // Dedupe: SNS at-least-once delivery + multi-connection fan-out means
      // this notification can arrive on the socket more than once for the
      // SAME upload run. handledMismatchKeys is module-level so it survives
      // StrictMode double-invokes and HMR cycles.
      const dedupKey = aiProgressMessage.combinedETag;
      if (dedupKey && handledMismatchKeys.has(dedupKey)) {
        logger.info('Ignoring duplicate address-mismatch for already-handled run', { dedupKey });
        return;
      }
      if (dedupKey) rememberDedupKey(handledMismatchKeys, dedupKey);

      // SnsClientWrapper spreads additionalPayload into the message top level,
      // so read these as top-level fields. Fall back to additionalPayload for
      // safety in case that ever changes.
      const top = aiProgressMessage as AiProgressMessage;
      const documentId = top.documentId ?? (additionalPayload?.documentId as string | undefined);

      logger.info('Address mismatch detected — opening confirmation modal', { aiProgressMessage });
      const documentRecordId =
        top.documentRecordId ?? (additionalPayload?.documentRecordId as string | undefined);
      const documentName =
        top.documentName ?? (additionalPayload?.documentName as string | undefined);
      const resolvedPropId =
        top.propertyId ?? (additionalPayload?.propertyId as string | undefined);
      const extracted =
        top.extractedAddress ?? (additionalPayload?.extractedAddress as string | undefined) ?? null;
      const propertyAddr =
        top.propertyAddress ?? (additionalPayload?.propertyAddress as string | undefined) ?? '';
      const confidence =
        top.addressConfidence ?? (additionalPayload?.addressConfidence as number | undefined) ?? 0;
      const threshold =
        top.confidenceThreshold ??
        (additionalPayload?.confidenceThreshold as number | undefined) ??
        0.5;
      const reasonText =
        top.reason ?? (additionalPayload?.reason as string | undefined) ?? 'Address mismatch';

      // Open the modal even if documentId is missing — the user still needs to
      // know processing was halted. The override button will be disabled when
      // there's no documentId to call back with.
      setAddressMismatchPayload({
        documentId: documentId ?? '',
        documentRecordId,
        documentName,
        propertyId: resolvedPropId ?? '',
        extractedAddress: extracted,
        propertyAddress: propertyAddr,
        addressConfidence: confidence,
        confidenceThreshold: threshold,
        reason: reasonText,
      });
      setOverrideError(undefined);
      setIsPropertyProcessingInProgress(false);
      setStepProcessing(0);

      // The DocumentRecord just transitioned to NEEDS_CONFIRMATION on the
      // backend. With our 30-min global staleTime, react-query will happily
      // serve cached data without addressCheckStatus until the TTL expires —
      // so explicitly invalidate the queries that drive the pending-chip UI
      // (advisor properties view + admin properties tab).
      if (resolvedPropId) {
        queryClient.invalidateQueries({ queryKey: ['document-records', resolvedPropId] });
      }
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
      return;
    }

    if (
      code === AiProgressMessageCode.PULSEAI_JOB_STARTED ||
      code === AiProgressMessageCode.TEXTRACT_JOB_STARTED
    ) {
      setStepProcessing(2);
    }

    if (code === AiProgressMessageCode.AI_JOB_STARTED) {
      setStepProcessing(3);
    }

    if (code === AiProgressMessageCode.AI_JOB_FINISHED && isSuccess) {
      setStreamingIssuesCount(issueCountValue);
      setStreamingProperty(streamingPropertyValue);
    }

    if (
      code === AiProgressMessageCode.AI_JOB_CONTENT_UPDATED &&
      issueCountValue &&
      streamingPropertyValue &&
      issueValue
    ) {
      setStepProcessing(4);
      setStreamingIssuesCount(issueCountValue);
      setStreamingIssue(issueValue);
      setStreamingProperty(streamingPropertyValue);
    }

    if (isError && end) {
      logger.error(`bedrock progress message error: ${JSON.stringify(aiProgressMessage)}`);
      handleDocumentProcessingError(userMessage);
      return;
    }

    if (isSuccess && end) {
      logger.debug(`bedrock progress message success: ${JSON.stringify(aiProgressMessage)}`);
      handleDocumentProcessingSuccess(resolvedPropertyId);
    }
  }, [aiProgressMessage]);

  useEffect(() => {
    if (
      isPropertyProcessingSuccess &&
      isPostPropertyProcessingStarted &&
      !isPostPropertyProcessingDone
    ) {
      handlePostProcessingSuccess();
    }
  }, [
    properties,
    propertyIssues,
    isPropertyProcessingSuccess,
    isPostPropertyProcessingStarted,
    isPostPropertyProcessingDone,
  ]);

  // Keep the ref in sync so async callbacks can read the current value.
  useEffect(() => {
    isProcessingSuccessRef.current = isPropertyProcessingSuccess;
  }, [isPropertyProcessingSuccess]);

  // Sync-on-reconnect: when the WebSocket reconnects while processing is active,
  // immediately fetch the latest job state to catch any completion messages that
  // were sent during the disconnect window (API Gateway doesn't buffer/replay).
  useEffect(() => {
    if (
      connectionAttempt === lastSyncedAttempt.current ||
      !isProcessingActive ||
      !userId ||
      !processingStartTime || // require a session start — prevents matching stale historical jobs
      isPropertyProcessingSuccess
    ) {
      return;
    }

    lastSyncedAttempt.current = connectionAttempt;

    // Capture values at effect-run time for the async callback.
    const startSeconds = Math.floor(processingStartTime / 1000) - 120;

    queryClient
      .fetchQuery({
        queryKey: ['processing-jobs-reconnect-sync', userId, connectionAttempt],
        queryFn: () => searchDocumentProcessingJobsByUser(userId),
      })
      .then((jobs) => {
        // Re-check via ref in case WebSocket delivered the result while the
        // fetch was in-flight, to avoid double-handling completion.
        if (isProcessingSuccessRef.current) return;

        const terminalJob = jobs
          .filter(
            (job) =>
              job.createdAt >= startSeconds &&
              job.state !== JobState.inProgress &&
              job.jobType === DocumentProcessingJobType.InspectionSummary,
          )
          .sort((a, b) => b.createdAt - a.createdAt)[0];

        if (!terminalJob) return;

        if (terminalJob.state === JobState.completed) {
          logger.debug('Reconnect sync: found completed job', { jobId: terminalJob.id });
          handleDocumentProcessingSuccess(terminalJob.propertyId);
        } else if (terminalJob.state === JobState.failed) {
          logger.debug('Reconnect sync: found failed job', { jobId: terminalJob.id });
          handleDocumentProcessingError(terminalJob.messages?.[0]?.text);
        }
      })
      .catch(() => {
        // Sync failed — spinner stays active; the next reconnect will try again.
        logger.warn('Reconnect sync: job status fetch failed');
      });
  }, [
    connectionAttempt,
    isProcessingActive,
    userId,
    isPropertyProcessingSuccess,
    processingStartTime,
    queryClient,
  ]);

  // Debug effect for streaming text changes
  useEffect(() => {
    if (streamingIssuesCount !== undefined || streamingProperty !== undefined) {
      logger.debug(`StreamingProperty state updated in UploaderProvider: ${streamingProperty}`);
    }
  }, [streamingProperty, streamingIssuesCount]);

  // Bootstraps the sidebar ProcessingIndicator for upload paths that don't
  // flow through startFileUpload (e.g. AddPropertyModal).
  const beginProcessingIndicator = () => {
    setStepProcessing(1);
    setProcessingStartTime(Date.now());
    setIsPropertyProcessingSuccess(false);
    setLastProcessedPropertyId(undefined);
    setStreamingProperty(undefined);
    setStreamingIssuesCount(undefined);
    setAddressMismatchPayload(undefined);
    setOverrideError(undefined);
  };

  const handleAddressOverrideConfirm = async () => {
    if (!addressMismatchPayload) return;
    setIsOverrideSubmitting(true);
    setOverrideError(undefined);
    try {
      await confirmAddressOverride(addressMismatchPayload.documentId);
      // Re-running the state machine will fire fresh progress events; flip processing
      // back on so the UI keeps spinning rather than resolving prematurely.
      setIsPropertyProcessingInProgress(true);
      setStepProcessing(2);
      setAddressMismatchPayload(undefined);
    } catch (err) {
      logger.error('Address override failed', { err });
      setOverrideError(err instanceof Error ? err.message : 'Failed to resume processing');
    } finally {
      setIsOverrideSubmitting(false);
    }
  };

  const handleAddressOverrideCancel = async () => {
    const documentId = addressMismatchPayload?.documentId;
    const recordId = addressMismatchPayload?.documentRecordId;
    const propertyIdToInvalidate = addressMismatchPayload?.propertyId;
    // Close the modal + reset processing state immediately for snappy UX —
    // the actual delete runs in the background. If it fails the user can
    // still resolve the orphan via the Pending Approval row in admin/advisor.
    setAddressMismatchPayload(undefined);
    setOverrideError(undefined);
    setStepProcessing(0);
    setIsPropertyProcessingInProgress(false);
    setIsPropertyProcessingSuccess(false);
    setUploadedDocuments([]);

    const invalidate = () => {
      if (propertyIdToInvalidate) {
        queryClient.invalidateQueries({
          queryKey: ['document-records', propertyIdToInvalidate],
        });
      }
      // Parity with the cancel handlers in PropertiesTab/PropertiesPageNew —
      // the admin Properties tab pulls its pending-row data from this query.
      queryClient.invalidateQueries({ queryKey: ADMIN_PROPERTIES_QUERY_KEY });
    };

    if (documentId) {
      try {
        await cancelPendingUpload(documentId);
        invalidate();
        showAlert('Upload cancelled — document removed from the system.', 'success');
        return;
      } catch (err) {
        logger.error('Failed to cancel pending upload', { err });
        // 409 = another reviewer already resolved this row (override-accepted
        // or already cancelled). Refresh state instead of showing a scary toast.
        if (isApiError(err) && err.response?.status === 409) {
          invalidate();
          showAlert('This upload was already resolved by another reviewer.', 'info');
          return;
        }
      }
    }

    // Fallback: if we don't have a documentId (legacy payload) or the full
    // wipe failed, at least clean up the per-property record.
    if (recordId) {
      try {
        await deleteDocumentRecord({ id: recordId });
        invalidate();
        showAlert('Upload cancelled — pending record removed.', 'success');
      } catch (err) {
        logger.error('Failed to delete pending DocumentRecord on cancel', { err });
        showAlert(
          "Upload cancelled, but we couldn't fully clean up. You can remove it from the property's documents list.",
          'warning',
        );
      }
    } else {
      showAlert('Upload cancelled.', 'warning');
    }
  };

  // Function to reset processing state
  const resetProcessing = () => {
    setStepProcessing(0);
    setProcessingStartTime(undefined);
    setIsPropertyProcessingInProgress(false);
    setIsPropertyProcessingSuccess(false);
    setIsPostPropertyProcessingDone(false);
    setStreamingProperty(undefined);
    setStreamingIssuesCount(undefined);
    setUploadedDocuments([]);
    logger.debug('Processing state has been reset');
  };

  return (
    <UploaderContext.Provider
      value={{
        uploadedDocuments,
        removeUploadedDocument,
        startFileUpload,
        uploadProgress,
        isFileUploadInProgress,
        isFileUploadSuccess,
        startPropertyProcessing,
        beginProcessingIndicator,
        resetProcessing,
        lastProcessedPropertyId,
        isPropertyProcessingInProgress,
        isPropertyProcessingSuccess,
        isPostPropertyProcessingDone,
        stepProcessing,
        streamingIssue,
        streamingProperty,
        streamingIssuesCount,
      }}>
      {children}
      <AddressMismatchDialog
        open={!!addressMismatchPayload}
        payload={addressMismatchPayload}
        isLoading={isOverrideSubmitting}
        error={overrideError}
        onConfirm={handleAddressOverrideConfirm}
        onCancel={handleAddressOverrideCancel}
        onDismiss={() => {
          // × / ESC — defer the decision. Leave the pending record in place
          // so another reviewer (advisor/admin) can resolve it later.
          setAddressMismatchPayload(undefined);
          setOverrideError(undefined);
          setStepProcessing(0);
          setIsPropertyProcessingInProgress(false);
        }}
      />
    </UploaderContext.Provider>
  );
};
